import { useEffect, useState } from 'react';
import { F, R } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { getAiConsent, revokeAiConsent } from '../services/aiConsent';
import InfoBanner from './InfoBanner';

// Datenschutz-Übersicht: zeigt den aktuellen Consent-Status, erklärt
// Verarbeitung und ermöglicht Widerruf. Wird in die Einstellungen eingehängt.
export default function PrivacyNotice({ uid }) {
  const { T } = useTheme();
  const { t } = useT();
  const [consent, setConsent] = useState(undefined); // undefined = lädt
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!uid) { setConsent(null); return; }
    getAiConsent(uid).then(c => { if (!cancelled) setConsent(c); });
    return () => { cancelled = true; };
  }, [uid]);

  async function handleRevoke() {
    if (!uid) return;
    setWorking(true);
    await revokeAiConsent(uid);
    setConsent(null);
    setWorking(false);
  }

  const grantedAt = consent?.grantedAt?.toDate
    ? consent.grantedAt.toDate().toLocaleDateString('de-DE')
    : null;

  return (
    <div style={{ padding: '20px 16px 100px', color: T.text, fontFamily: F.family }}>
      <div style={{
        fontFamily: F.display, fontSize: 22, fontWeight: 600,
        letterSpacing: '-0.03em', marginBottom: 4,
      }}>
        {t('privacy')}
      </div>
      <div style={{ fontSize: 12, color: T.sub, marginBottom: 18 }}>
        {t('con_privacy_sub')}
      </div>

      <Section T={T} title={t('con_priv_ai_title')}>
        {t('con_priv_ai_body_pre')}<strong>Google Gemini</strong>{t('con_priv_ai_body_post')}
      </Section>

      <Section T={T} title={t('con_priv_local_title')}>
        {t('con_priv_local_body')}
      </Section>

      <Section T={T} title={t('con_priv_store_title')}>
        {t('con_priv_store_body_pre')}<code>users/{uid || '{deine-id}'}</code>{t('con_priv_store_body_post')}
      </Section>

      <Section T={T} title={t('con_priv_retention_title')}>
        {t('con_priv_retention_body')}
      </Section>

      <Section T={T} title={t('con_priv_rights_title')}>
        {t('con_priv_rights_body')}
      </Section>

      <div style={{ marginTop: 20 }}>
        {consent === undefined ? (
          <InfoBanner tone="info" title={t('con_priv_consent_loading')} />
        ) : consent ? (
          <InfoBanner tone="success" title={t('con_priv_consent_active')}>
            {t('con_priv_consent_active_body', { date: grantedAt || '—' })}
          </InfoBanner>
        ) : (
          <InfoBanner tone="info" title={t('con_priv_consent_none')}>
            {t('con_priv_consent_none_body')}
          </InfoBanner>
        )}

        {consent && (
          <button
            onClick={handleRevoke}
            disabled={working}
            style={{
              marginTop: 14, width: '100%', padding: '12px',
              background: 'none',
              border: `1px solid ${T.line || T.border}`,
              boxShadow: `inset 3px 0 0 0 ${T.red}`,
              borderRadius: R.md,
              color: T.text, fontFamily: F.family,
              fontSize: 13, fontWeight: 600, cursor: working ? 'default' : 'pointer',
            }}
          >
            {working ? t('con_priv_revoking') : t('con_priv_revoke')}
          </button>
        )}
      </div>
    </div>
  );
}

function Section({ T, title, children }) {
  return (
    <div style={{
      marginBottom: 14,
      padding: '14px 16px',
      background: T.surface,
      border: `1px solid ${T.line || T.border}`,
      borderRadius: R.md,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 700, color: T.text,
        marginBottom: 5, letterSpacing: '0.02em',
      }}>
        {title}
      </div>
      <div style={{ fontSize: 12.5, color: T.sub, lineHeight: 1.55 }}>
        {children}
      </div>
    </div>
  );
}
